"use client"

import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { ArrowRight, Loader2, Lock, User, Eye, EyeOff, ShieldCheck } from "lucide-react"
import { signInWithEmailAndPassword } from "firebase/auth"
import { auth, db } from "@/lib/firebase"
import { collection, query, where, getDocs } from "firebase/firestore"

const LoginForm = () => {
  const navigate = useNavigate()
  const [identifier, setIdentifier] = useState("") // Can be email or phone
  const [password, setPassword] = useState("")
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState("")

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!identifier || !password) return
    setError("")
    setLoading(true)

    try {
      let finalEmail = identifier;

      // Logic to check if identifier is a mobile number (no @ symbol)
      if (!identifier.includes("@")) {
        const q = query(collection(db, "users"), where("phone", "==", identifier))
        const querySnapshot = await getDocs(q)
        
        if (querySnapshot.empty) {
          setError("Account not found. Please verify your mobile number.")
          setLoading(false)
          return
        }
        
        finalEmail = querySnapshot.docs[0].data().email
      }

      await signInWithEmailAndPassword(auth, finalEmail, password)
      navigate("/profile")
    } catch (error: any) {
      console.error(error)
      setError("Authentication failed. Please verify your credentials and try again.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="bg-white border border-slate-200 p-8 sm:p-10 shadow-2xl">
      <div className="flex flex-col items-center text-center mb-10 space-y-4">
        <div className="w-28 h-28 flex items-center justify-center overflow-hidden">
           <img src="/logo-final.png" alt="Rohith's Academy" className="w-full h-full object-cover" />
        </div>
        <div>
          <p className="text-[10px] font-bold text-[#b48c36] uppercase tracking-[0.4em] mb-2">Welcome Back</p>
          <h1 className="text-3xl font-serif font-medium text-slate-950 leading-tight">
            Sign In
          </h1>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-600 text-[10px] font-bold uppercase tracking-widest text-center">
          {error}
        </div>
      )}

      <form onSubmit={handleLogin} className="space-y-6">
        <div className="space-y-2">
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block text-center">Email or Mobile Number</label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              required
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 py-4 pl-12 pr-4 text-sm focus:outline-none focus:border-[#b48c36] transition-colors placeholder:text-slate-300"
              placeholder="Email or Mobile"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block text-center">Password</label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type={showPassword ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 py-4 pl-12 pr-12 text-sm focus:outline-none focus:border-[#b48c36] transition-colors placeholder:text-slate-300"
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#b48c36] transition-colors"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <div className="flex justify-end pt-2">
            <button type="button" className="text-[10px] text-[#b48c36] hover:text-slate-900 font-bold uppercase tracking-widest transition-colors">
              Forgot password?
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-slate-950 text-white p-4 text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-[#b48c36] transition-colors flex items-center justify-center disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
        </button>
      </form>

      <div className="mt-10 pt-8 border-t border-slate-100 text-center">
        <p className="text-xs text-slate-500 font-sans mb-3">Don't have an account?</p>
        <Link to="/signup" className="text-[10px] font-bold text-slate-950 uppercase tracking-widest hover:text-[#b48c36] transition-colors inline-flex items-center gap-1 group border-b border-transparent hover:border-[#b48c36] pb-1">
          Sign Up Here <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </div>
  )
}

export default LoginForm
